import { nanoid } from 'nanoid';
import {
  pipe,
  filter,
  propEq,
  map,
  reduce,
  reduced,
  curry,
  pathEq,
  concat,
  lensPath,
  set,
} from 'ramda';
import { CARD_TYPES, CARD_ACTION_TYPES } from '../../constants';

export const ENTITIES = {
  BLOCK: 'block',
  CARD: 'card',
  COL: 'col',
  GRID: 'grid',
  LAYOUT: 'layout',
  NODE: 'node',
  ROW: 'row',
};

const getLayoutEntityMethods = (type, mode) => ({
  getChildren: (item, data) =>
    pipe(
      map(childId => data.entities.layoutItems[childId]),
      filter(Boolean),
    )(item.items ?? []),
  isOneOfType: propEq('mode', mode),
  prop: mode === 'layout' ? 'layout' : 'layoutItems',
  type,
});

export const nodeIdLinkReplacer = nodeId => nodeId?.replace?.('LINK::', '');

export const entitiesMethods = {
  [ENTITIES.LAYOUT]: getLayoutEntityMethods(ENTITIES.LAYOUT, 'layout'),
  [ENTITIES.GRID]: getLayoutEntityMethods(ENTITIES.GRID, 'grid'),
  [ENTITIES.ROW]: getLayoutEntityMethods(ENTITIES.ROW, 'row'),
  [ENTITIES.COL]: getLayoutEntityMethods(ENTITIES.COL, 'col'),
  [ENTITIES.BLOCK]: {
    ...getLayoutEntityMethods(ENTITIES.COL, 'block'),
    getChildren: (item, data) =>
      item.data && data.entities.cards[item.data]
        ? [data.entities.cards[item.data]]
        : [],
  },
  [ENTITIES.CARD]: {
    getChildren: (item, data) =>
      concat(
        pipe(
          map(nodeId => data.entities.nodes[nodeIdLinkReplacer(nodeId)]),
          filter(Boolean),
        )(item.nodes ?? []),
        map(variant => data.entities.cards[variant])(item.variants ?? []),
      ),
    isOneOfType: (item, data) => !!data.entities.cards?.[item.id],
    prop: 'cards',
    type: ENTITIES.CARD,
  },
  [ENTITIES.NODE]: {
    getChildren: (item, data) =>
      map(nodeId => data.entities.nodes[nodeId], item?.nodes ?? []),
    isOneOfType: (item, data) =>
      !data.entities.cards?.[item.id] && !!data.entities.nodes?.[item.id],
    prop: 'nodes',
    type: ENTITIES.NODE,
  },
};

const getTypeOfNode = (item, data) =>
  pipe(Object.values, arr =>
    arr.find(entity => entitiesMethods[entity].isOneOfType(item, data)),
  )(ENTITIES);

export const getLayout = data => Object.values(data?.entities?.layout ?? {})[0];

export const findTreeNode = curry((entityType, predicate, parent, data) => {
  parent = parent || getLayout(data);
  const parentType = getTypeOfNode(parent, data);
  const children = entitiesMethods[parentType].getChildren(parent, data);
  return reduce(
    (result, item) => {
      const itemType = getTypeOfNode(item, data);
      if (itemType === entityType && predicate(item)) {
        return reduced(item);
      }
      const foundNode = findTreeNode(entityType, predicate, item, data);
      return foundNode ? reduced(foundNode) : undefined;
    },
    undefined,
    children,
  );
});

export const findCard = findTreeNode(ENTITIES.CARD);
export const findLocalNav = findCard(
  pathEq(['properties', 'containerType'], CARD_TYPES.LOCAL_NAV),
);
export const findMerchMenu = findCard(
  pathEq(['properties', 'containerType'], CARD_TYPES.MERCH_MENU),
);
export const findTitle = findCard(propEq('subType', CARD_TYPES.TITLE));
export const findDynamicRelatedContent = findCard(
  pathEq(['properties', 'containerType'], CARD_TYPES.DYNAMIC_RELATED_FILMSTRIP),
);
export const findRelatedContent = findCard(
  pathEq(['properties', 'containerType'], CARD_TYPES.RELATED_FILMSTRIP),
);
export const findArticleFooter = findCard(
  propEq('subType', CARD_TYPES.ARTICLE_FOOTER),
);

export const findMetaBox = findCard(propEq('subType', CARD_TYPES.META_BOX));

export const filterTreeNodes = curry((entityType, predicate, parent, data) => {
  parent = parent || getLayout(data);
  const parentType = getTypeOfNode(parent, data);
  const children = entitiesMethods[parentType].getChildren(parent, data);
  return reduce(
    (result, item) => {
      const itemType = getTypeOfNode(item, data);
      return filter(Boolean, [
        ...result,
        itemType === entityType && predicate(item) ? item : undefined,
        ...filterTreeNodes(entityType, predicate, item, data),
      ]);
    },
    [],
    children,
  );
});

// Vanilla JS implementation of Ramda's mergeDeepRight
// Recursively merges two objects, with values from obj2 taking precedence
export const mergeDeepRight = (obj1, obj2) => {
  // Helper to check if value is a plain object (not array, null, etc)
  const isPlainObject = val =>
    val !== null && typeof val === 'object' && !Array.isArray(val);

  // Base case: if either is not a plain object, return obj2
  if (!isPlainObject(obj1) || !isPlainObject(obj2)) {
    return obj2;
  }

  // Merge: start with all keys from obj1
  const result = { ...obj1 };

  // Process each key from obj2
  Object.keys(obj2).forEach(key => {
    if (isPlainObject(obj1[key]) && isPlainObject(obj2[key])) {
      // Both are plain objects - merge recursively
      result[key] = mergeDeepRight(obj1[key], obj2[key]);
    } else {
      // Otherwise, obj2's value wins
      result[key] = obj2[key];
    }
  });

  return result;
};
export const updateTreeNode = (item, changes, data) => {
  if (!item?.id) {
    return data;
  }
  const type = getTypeOfNode(item, data);
  return {
    ...data,
    entities: {
      ...data.entities,
      [entitiesMethods[type].prop]: {
        ...data.entities[entitiesMethods[type].prop],
        [item.id]: mergeDeepRight(item, changes),
      },
    },
  };
};

const ZERO_OFFSET = { large: 0, medium: 0, small: 0 };
const FULL_COL_WIDTH = 12;
const getColSpan = offset => FULL_COL_WIDTH - offset;

// creates 12-column grid with one card
export const createGridWithCard = (
  card,
  { fluid = false, offset = ZERO_OFFSET, span, gridIdPrefix = '' } = {},
) => {
  const gridId = `${gridIdPrefix}${nanoid()}`;
  const rowId = `${nanoid()}-row`;
  const colId = `${nanoid()}-col`;
  const blockId = `${nanoid()}-block`;
  return {
    data: {
      entities: {
        cards: {
          [card.id]: card,
        },
        layoutItems: {
          [blockId]: { data: card.id, id: blockId, mode: 'block' },
          [colId]: {
            id: colId,
            items: [blockId],
            mode: 'col',
            offset,
            span: span || {
              large: getColSpan(offset.large),
              medium: getColSpan(offset.medium),
              small: getColSpan(offset.small),
            },
          },
          [gridId]: {
            attributes: { fluid },
            display: { large: true, medium: true, small: true },
            id: gridId,
            items: [rowId],
            mode: 'grid',
          },
          [rowId]: {
            attributes: { gutter: true },
            id: rowId,
            items: [colId],
            mode: 'row',
          },
        },
      },
    },
    gridId,
  };
};

const removeTreeNodes = (entity, data) => {
  if (!entity) {
    return data;
  }

  const entityType = getTypeOfNode(entity, data);
  const entityMethods = entitiesMethods[entityType];
  const { prop: dataProp } = entityMethods;
  // This hack is needed to remove entity with a particular id but keep the rest object params
  const { [entity.id]: entityToRemove, ...updatedEntities } =
    data.entities[dataProp];
  const children = entitiesMethods[entityType].getChildren(entity, data);
  const updatedData = set(
    lensPath(['entities', dataProp]),
    { ...updatedEntities },
    data,
  );

  return reduce(
    (acc, child) => removeTreeNodes(child, acc),
    updatedData,
    children,
  );
};

const removeLayoutGridItem = (gridId, data) => {
  if (!gridId) {
    return data;
  }

  const layout = getLayout(data);
  const updatedLayout = {
    ...layout,
    items: layout.items.filter(id => id !== gridId),
  };

  return set(lensPath(['entities', 'layout', layout.id]), updatedLayout, data);
};

export const removeGridWithSpecificCardFromTree = (card, data) => {
  const grids = filterTreeNodes(
    ENTITIES.GRID,
    grid =>
      findTreeNode(ENTITIES.CARD, item => item.id === card.id, grid, data),
    null,
    data,
  );

  return reduce(
    (acc, grid) => removeLayoutGridItem(grid.id, removeTreeNodes(grid, acc)),
    data,
    grids,
  );
};

export const extractUrlIngredients = (ingredients, actionType) => {
  if (!ingredients) {
    return {};
  }

  // BUYING TOOLS Ingredients
  if (actionType === CARD_ACTION_TYPES.BUYING_TOOLS) {
    const buyingToolsIngredients = {};
    if (ingredients.productId !== undefined)
      buyingToolsIngredients.productId = ingredients.productId;
    if (ingredients.styleColor !== undefined)
      buyingToolsIngredients.styleColor = ingredients.styleColor;
    return { buyingToolsIngredients };
  }

  // URL Ingredients
  const urlIngredients = {};
  if (ingredients.conceptIds !== undefined)
    urlIngredients.conceptIds = ingredients.conceptIds;
  if (ingredients.pageId !== undefined)
    urlIngredients.pageId = ingredients.pageId;
  if (ingredients.pdpType !== undefined)
    urlIngredients.pdpType = ingredients.pdpType;
  if (ingredients.prebuildId !== undefined)
    urlIngredients.prebuildId = ingredients.prebuildId;
  if (ingredients.styleColor !== undefined)
    urlIngredients.styleColor = ingredients.styleColor;
  if (ingredients.type !== undefined) urlIngredients.type = ingredients.type;

  return { urlIngredients };
};
